// Converts a USD price value into the visitor's local currency and
// splits the formatted output into "symbol" vs "number" chunks so
// callers can keep the existing two-tone price typography.

function convertAndFormat(usdValue, { locale, currencyCode, rate }) {
  const numeric = typeof usdValue === 'number' ? usdValue : parseFloat(usdValue);
  if (Number.isNaN(numeric)) {
    return { formatted: `$${usdValue}`, converted: null };
  }

  const hasDecimals = String(usdValue).includes('.');
  const converted = numeric * rate;
  const options = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  };

  try {
    const nf = new Intl.NumberFormat(locale, options);
    return { formatted: nf.format(converted), converted, parts: nf.formatToParts(converted) };
  } catch {
    const fallback = converted.toFixed(hasDecimals ? 2 : 0);
    return { formatted: `${currencyCode} ${fallback}`, converted };
  }
}

// Groups Intl.NumberFormat parts into ordered {kind: 'symbol'|'number', value} chunks,
// preserving locale-correct symbol placement (prefix or suffix).
export function formatPriceChunks(usdValue, ctx) {
  const { formatted, parts } = convertAndFormat(usdValue, ctx);
  if (!parts) return [{ kind: 'number', value: formatted }];

  const chunks = [];
  for (const part of parts) {
    const kind = part.type === 'currency' ? 'symbol' : 'number';
    const last = chunks[chunks.length - 1];
    if (last && last.kind === kind) {
      last.value += part.value;
    } else {
      chunks.push({ kind, value: part.value });
    }
  }
  return chunks;
}

export function formatPrice(usdValue, ctx) {
  return convertAndFormat(usdValue, ctx).formatted;
}

// Rewrites embedded price text such as "Plans starting at $0.99/month." or
// standalone "$1.99/month", as well as French/German style "1,99 $/mois".
// Replaces ALL occurrences anywhere in the string, preserving surrounding text.
export function convertPriceString(text, ctx) {
  if (!text) return text;

  // Match "$X.XX" or "$X" anywhere in the string (global replace)
  const leadingGlobal = /\$\s*([\d.,]+)/g;
  if (leadingGlobal.test(text)) {
    return text.replace(/\$\s*([\d.,]+)/g, (_, amount) => {
      return formatPrice(amount.replace(/,/g, ''), ctx);
    });
  }

  // Trailing symbol with comma decimals anywhere: "1,99 $/mois"
  const trailingGlobal = /([\d.,]+)\s*\$/g;
  if (trailingGlobal.test(text)) {
    return text.replace(/([\d.,]+)\s*\$/g, (_, amount) => {
      const normalized = amount.replace(/\./g, '').replace(',', '.');
      return formatPrice(normalized, ctx);
    });
  }

  return text;
}
